'use client';

import { BigNum, MarketType, QUOTE_PRECISION_EXP } from '@drift-labs/sdk';
import { ENUM_UTILS, MarketId } from '@drift/common';
import React, { useEffect, useState } from 'react';
import use24hMarketDataFromStore from 'src/hooks/use24hMarketDataFromStore';
import {
	MARKET_STATS_BAR_CELL_VALUE_CLASSNAME,
	MARKET_STATS_BAR_CELL_VALUE_SKELETON_CLASSNAME,
	MARKET_STATS_BAR_CELL_LABEL_CLASSNAME,
} from 'src/components/MarketStatsBar/consts';
import StatDisplay from './StatDisplay';
import { DriftWindow } from '../../window/driftWindow';
import NumberDisplay from 'src/components/NumberDisplay/NumberDisplay';

const _Rolling24HrVolumeNumberDisplay = ({
	marketType,
	marketIndex,
	noLabel,
}: {
	marketType?: MarketType;
	marketIndex?: number;
	noLabel?: boolean;
}) => {
	const marketId = new MarketId(
		marketIndex ?? 0,
		marketType ?? MarketType.SPOT
	);

	const { hasLoadedInitialData, volume } = useRolling24HVolume(marketId);

	const content = (
		<NumberDisplay
			value={BigNum.fromPrint(volume.toString(), QUOTE_PRECISION_EXP)}
			type="currency"
			className={MARKET_STATS_BAR_CELL_VALUE_CLASSNAME}
			loading={!hasLoadedInitialData}
			skeletonClassName={MARKET_STATS_BAR_CELL_VALUE_SKELETON_CLASSNAME}
			millify
		/>
	);

	if (noLabel) {
		return content;
	}

	return (
		<StatDisplay
			header="24h Volume"
			headerClassName={MARKET_STATS_BAR_CELL_LABEL_CLASSNAME}
			leftBorder
		>
			{content}
		</StatDisplay>
	);
};

export const useRolling24HVolume = (marketId: MarketId) => {
	const [volume, setVolume] = useState(0);
	const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
	const marketsData = use24hMarketDataFromStore();

	useEffect(() => {
		const getAndSetVolumeData = () => {
			if (marketsData && marketsData.length !== 0) {
				const selectedMarketData = marketsData.find(
					(mkt) =>
						mkt.marketIndex === marketId.marketIndex &&
						ENUM_UTILS.match(mkt.marketType, marketId.marketType)
				);

				if (selectedMarketData) {
					setVolume(selectedMarketData.quoteVolume ?? 0);
					setHasLoadedInitialData(true);
					DriftWindow.recordStartupTimeMetric('marketStatsLoaded');
				} else {
					setHasLoadedInitialData(false);
				}
			}
		};

		getAndSetVolumeData();

		const interval = setInterval(getAndSetVolumeData, 1000 * 60);

		return () => {
			clearInterval(interval);
		};
	}, [
		marketId.marketIndex,
		ENUM_UTILS.toStr(marketId.marketType),
		marketsData,
	]);

	return {
		hasLoadedInitialData,
		volume,
	};
};

export const Rolling24HrVolumeNumberDisplay = React.memo(
	_Rolling24HrVolumeNumberDisplay
);
